import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  createCourse,
  getApiError,
  getCourse,
  listGurus,
  listMusicFields,
  listOrganizations,
  updateCourse,
} from "../../services/courseApi";
import {
  countSessions,
  emptySession,
  emptySubtopic,
  emptyTopic,
  fromCourse,
  initialCourseForm,
  toCoursePayload,
} from "./courseFormUtils";
import "./course.css";

function renumber(items) {
  return items.map((item, index) => ({ ...item, order: index + 1 }));
}

function move(items, index, direction) {
  const target = index + direction;
  if (target < 0 || target >= items.length) return items;
  const copy = [...items];
  [copy[index], copy[target]] = [copy[target], copy[index]];
  return renumber(copy);
}

function entityId(entity) {
  return entity.organization_id ?? entity.id ?? entity.user_id;
}

function entityName(entity) {
  return (
    entity.name ||
    [entity.first_name, entity.last_name].filter(Boolean).join(" ") ||
    entity.email ||
    `#${entityId(entity)}`
  );
}

function Field({ label, required, help, children }) {
  return (
    <label className="course-form-field">
      <span>{label}{required && <em> *</em>}</span>
      {children}
      {help && <small>{help}</small>}
    </label>
  );
}

function ItemActions({ index, length, onMove, onRemove, removeDisabled }) {
  return (
    <div className="course-item-actions">
      <button type="button" title="Move up" disabled={index === 0} onClick={() => onMove(-1)}>
        <i className="bi bi-arrow-up" />
      </button>
      <button type="button" title="Move down" disabled={index === length - 1} onClick={() => onMove(1)}>
        <i className="bi bi-arrow-down" />
      </button>
      <button type="button" className="danger" title="Remove" disabled={removeDisabled} onClick={onRemove}>
        <i className="bi bi-trash" />
      </button>
    </div>
  );
}

export default function CourseBuilder() {
  const { courseId } = useParams();
  const edit = Boolean(courseId);
  const navigate = useNavigate();
  const [form, setForm] = useState(initialCourseForm);
  const [version, setVersion] = useState(null);
  const [musicFields, setMusicFields] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [gurus, setGurus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const sessionCount = useMemo(() => countSessions(form.topics), [form.topics]);
  const matchingGurus = useMemo(
    () =>
      gurus.filter(
        (guru) =>
          !form.music_field_id || Number(guru.music_field_id) === Number(form.music_field_id)
      ),
    [gurus, form.music_field_id]
  );

  useEffect(() => {
    async function initialise() {
      setLoading(true);
      try {
        const [fields, orgs, guruUsers, course] = await Promise.all([
          listMusicFields(),
          listOrganizations(),
          listGurus(),
          edit ? getCourse(courseId) : Promise.resolve(null),
        ]);
        setMusicFields(Array.isArray(fields) ? fields : []);
        setOrganizations(Array.isArray(orgs) ? orgs : []);
        setGurus(Array.isArray(guruUsers) ? guruUsers : []);
        if (course) {
          setForm(fromCourse(course));
          setVersion(course.version);
        }
      } catch (error) {
        toast.error(getApiError(error, "Could not prepare the course form"));
      } finally {
        setLoading(false);
      }
    }
    initialise();
  }, [courseId, edit]);

  function setValue(name, value) {
    setForm((current) => ({ ...current, [name]: value }));
  }

  function setTopic(topicIndex, changes) {
    setForm((current) => ({
      ...current,
      topics: current.topics.map((topic, index) =>
        index === topicIndex ? { ...topic, ...changes } : topic
      ),
    }));
  }

  function setSubtopic(topicIndex, subtopicIndex, changes) {
    const topic = form.topics[topicIndex];
    setTopic(topicIndex, {
      subtopics: topic.subtopics.map((subtopic, index) =>
        index === subtopicIndex ? { ...subtopic, ...changes } : subtopic
      ),
    });
  }

  function setSession(topicIndex, subtopicIndex, sessionIndex, changes) {
    const subtopic = form.topics[topicIndex].subtopics[subtopicIndex];
    setSubtopic(topicIndex, subtopicIndex, {
      sessions: subtopic.sessions.map((session, index) =>
        index === sessionIndex ? { ...session, ...changes } : session
      ),
    });
  }

  function addTopic() {
    setValue("topics", [...form.topics, emptyTopic(form.topics.length + 1)]);
  }

  function addSubtopic(topicIndex) {
    const topic = form.topics[topicIndex];
    setTopic(topicIndex, {
      subtopics: [...topic.subtopics, emptySubtopic(topic.subtopics.length + 1)],
    });
  }

  function addSession(topicIndex, subtopicIndex) {
    const subtopic = form.topics[topicIndex].subtopics[subtopicIndex];
    setSubtopic(topicIndex, subtopicIndex, {
      sessions: [
        ...subtopic.sessions,
        { ...emptySession(subtopic.sessions.length + 1), duration_minutes: form.session_duration_minutes },
      ],
    });
  }

  function validate() {
    if (!form.title.trim()) return "Course title is required";
    if (!edit && !form.music_field_id) return "Music field is required";
    if (!edit && !form.provider.id) return "Course provider is required";
    if (form.price.amount === "" || Number(form.price.amount) < 0) return "Enter a valid price";
    if (Number(form.validity_months) < 1) return "Validity must be at least one month";
    if (Number(form.session_duration_minutes) < 1) return "Session duration is required";
    if (!form.topics.length) return "Add at least one topic";
    for (const topic of form.topics) {
      if (!topic.title.trim()) return "Every topic needs a title";
      if (!topic.subtopics.length) return `Add a subtopic under “${topic.title}”`;
      for (const subtopic of topic.subtopics) {
        if (!subtopic.title.trim()) return "Every subtopic needs a title";
        if (!subtopic.sessions.length) return `Add a session under “${subtopic.title}”`;
        if (subtopic.sessions.some((session) => !session.title.trim())) {
          return "Every curriculum session needs a title";
        }
      }
    }
    if (form.assignment_progress_gate === "MINIMUM_SCORE") {
      const maximum = Number(form.assignment_max_score);
      const minimum = Number(form.assignment_minimum_score);
      if (maximum <= 0 || minimum < 0 || minimum > maximum) {
        return "Minimum score must be between zero and the maximum score";
      }
    }
    return null;
  }

  async function submit(event) {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSaving(true);
    try {
      const payload = toCoursePayload(form, { edit, expectedVersion: version });
      const saved = edit
        ? await updateCourse(courseId, payload)
        : await createCourse(payload);
      toast.success(edit ? "New course version published" : "Course created and published");
      navigate(`/courses/${saved.course_id || courseId}`);
    } catch (error) {
      toast.error(getApiError(error, "Could not save course"));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="course-state"><div className="spinner-border text-primary" /></div>;
  }

  const providers = form.provider.type === "ORGANIZATION" ? organizations : matchingGurus;

  return (
    <main className="course-page course-builder-page">
      <Link className="course-back-link" to={edit ? `/courses/${courseId}` : "/courses"}>
        <i className="bi bi-arrow-left me-2" />{edit ? "Back to course" : "Back to courses"}
      </Link>

      <div className="course-page-header mt-3">
        <div>
          <p className="course-eyebrow">Course management</p>
          <h1>{edit ? "Edit course" : "Create course"}</h1>
          <p className="text-muted mb-0">
            {edit
              ? `Saving will publish version ${Number(version) + 1} without changing existing enrollments.`
              : "The course will be published immediately after creation."}
          </p>
        </div>
        <div className="course-session-count">
          <span>{sessionCount}</span> curriculum sessions
        </div>
      </div>

      <form onSubmit={submit}>
        <section className="course-panel">
          <h2>Ownership</h2>
          {edit && (
            <div className="alert alert-light border">
              Provider and music field belong to the course itself and cannot be changed in a new version.
            </div>
          )}
          <div className="course-form-grid three">
            <Field label="Provider type" required>
              <select
                className="form-select"
                disabled={edit}
                value={form.provider.type}
                onChange={(event) =>
                  setValue("provider", { type: event.target.value, id: "" })
                }
              >
                <option value="ORGANIZATION">Organization</option>
                <option value="GURU">Independent guru</option>
              </select>
            </Field>
            <Field label={form.provider.type === "ORGANIZATION" ? "Organization" : "Guru"} required>
              <select
                className="form-select"
                disabled={edit}
                value={form.provider.id}
                onChange={(event) =>
                  setValue("provider", { ...form.provider, id: event.target.value })
                }
              >
                <option value="">Select provider</option>
                {providers.map((provider) => (
                  <option key={entityId(provider)} value={entityId(provider)}>
                    {entityName(provider)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Music field" required help={form.provider.type === "GURU" ? "The guru must belong to this music field." : null}>
              <select
                className="form-select"
                disabled={edit}
                value={form.music_field_id}
                onChange={(event) => {
                  setValue("music_field_id", event.target.value);
                  if (!edit && form.provider.type === "GURU") {
                    setValue("provider", { ...form.provider, id: "" });
                  }
                }}
              >
                <option value="">Select music field</option>
                {musicFields.map((field) => (
                  <option key={field.id} value={field.id}>{field.name}</option>
                ))}
              </select>
            </Field>
          </div>
        </section>

        <section className="course-panel">
          <h2>Course information</h2>
          <div className="course-form-grid">
            <Field label="Course title" required>
              <input className="form-control" value={form.title} onChange={(e) => setValue("title", e.target.value)} />
            </Field>
            <Field label="Course type" required>
              <select className="form-select" value={form.type} onChange={(e) => setValue("type", e.target.value)}>
                <option value="GRADED">Graded</option>
                <option value="ONE_OFF">One-off</option>
              </select>
            </Field>
            <Field label="Short description">
              <input className="form-control" value={form.short_description} onChange={(e) => setValue("short_description", e.target.value)} />
            </Field>
            <Field label="Delivery mode" required>
              <select className="form-select" value={form.delivery_mode} onChange={(e) => setValue("delivery_mode", e.target.value)}>
                <option value="ONLINE">Online</option>
                <option value="IN_PERSON">In person</option>
                <option value="HYBRID">Online and in person</option>
              </select>
            </Field>
            <Field label="Description">
              <textarea className="form-control" rows="4" value={form.description} onChange={(e) => setValue("description", e.target.value)} />
            </Field>
            <div className="course-form-stack">
              <Field label="Thumbnail URL">
                <input className="form-control" type="url" value={form.thumbnail_url} onChange={(e) => setValue("thumbnail_url", e.target.value)} />
              </Field>
              <Field label="Hero image URL">
                <input className="form-control" type="url" value={form.hero_image_url} onChange={(e) => setValue("hero_image_url", e.target.value)} />
              </Field>
            </div>
          </div>
        </section>

        <section className="course-panel">
          <h2>Pricing and scheduling</h2>
          <div className="course-form-grid four">
            <Field label="Prepaid price" required>
              <div className="input-group">
                <span className="input-group-text">₹</span>
                <input className="form-control" type="number" min="0" step="0.01" value={form.price.amount} onChange={(e) => setValue("price", { ...form.price, amount: e.target.value })} />
              </div>
            </Field>
            <Field label="Validity (months)" required>
              <input className="form-control" type="number" min="1" value={form.validity_months} onChange={(e) => setValue("validity_months", e.target.value)} />
            </Field>
            <Field label="Default session duration" required>
              <div className="input-group">
                <input className="form-control" type="number" min="1" value={form.session_duration_minutes} onChange={(e) => setValue("session_duration_minutes", e.target.value)} />
                <span className="input-group-text">min</span>
              </div>
            </Field>
            <Field label="Cancellation limit" required>
              <input className="form-control" type="number" min="0" value={form.cancellation_limit} onChange={(e) => setValue("cancellation_limit", e.target.value)} />
            </Field>
          </div>
        </section>

        <section className="course-panel">
          <h2>Progress rules</h2>
          <div className="course-form-grid three">
            <Field label="Completion rule" required>
              <select className="form-select" value={form.completion_rule} onChange={(e) => setValue("completion_rule", e.target.value)}>
                <option value="ATTENDANCE">Attendance</option>
                <option value="ASSIGNMENT">Assignment</option>
                <option value="ATTENDANCE_AND_ASSIGNMENT">Attendance and assignment</option>
              </select>
            </Field>
            <Field label="Next-session assignment gate" required>
              <select className="form-select" value={form.assignment_progress_gate} onChange={(e) => setValue("assignment_progress_gate", e.target.value)}>
                <option value="SUBMITTED">Submitted</option>
                <option value="REVIEWED">Reviewed by guru</option>
                <option value="MINIMUM_SCORE">Minimum score</option>
              </select>
            </Field>
            {form.assignment_progress_gate === "MINIMUM_SCORE" && (
              <div className="course-score-fields">
                <Field label="Minimum score" required>
                  <input className="form-control" type="number" min="0" value={form.assignment_minimum_score} onChange={(e) => setValue("assignment_minimum_score", e.target.value)} />
                </Field>
                <Field label="Maximum score" required>
                  <input className="form-control" type="number" min="1" value={form.assignment_max_score} onChange={(e) => setValue("assignment_max_score", e.target.value)} />
                </Field>
              </div>
            )}
          </div>
        </section>

        <section className="course-panel">
          <div className="course-section-heading">
            <div>
              <h2>Curriculum</h2>
              <p className="text-muted mb-0">Build the ordered topic, subtopic and session hierarchy.</p>
            </div>
            <button className="btn btn-outline-primary" type="button" onClick={addTopic}>
              <i className="bi bi-plus-lg me-2" />Add topic
            </button>
          </div>

          <div className="course-curriculum-editor">
            {form.topics.map((topic, topicIndex) => (
              <details open key={`topic-${topicIndex}`} className="course-topic-editor">
                <summary>
                  <span className="course-order-number">{topicIndex + 1}</span>
                  <span className="course-editor-summary-title">{topic.title || "Untitled topic"}</span>
                  <ItemActions
                    index={topicIndex}
                    length={form.topics.length}
                    removeDisabled={form.topics.length === 1}
                    onMove={(direction) => setValue("topics", move(form.topics, topicIndex, direction))}
                    onRemove={() => setValue("topics", renumber(form.topics.filter((_, index) => index !== topicIndex)))}
                  />
                </summary>
                <div className="course-editor-body">
                  <div className="course-form-grid">
                    <Field label="Topic title" required>
                      <input className="form-control" value={topic.title} onChange={(e) => setTopic(topicIndex, { title: e.target.value })} />
                    </Field>
                    <Field label="Topic description">
                      <input className="form-control" value={topic.description || ""} onChange={(e) => setTopic(topicIndex, { description: e.target.value })} />
                    </Field>
                  </div>

                  <div className="course-subtopic-list">
                    {topic.subtopics.map((subtopic, subtopicIndex) => (
                      <div className="course-subtopic-editor" key={`subtopic-${topicIndex}-${subtopicIndex}`}>
                        <div className="course-subtopic-heading">
                          <h3>Subtopic {subtopicIndex + 1}</h3>
                          <ItemActions
                            index={subtopicIndex}
                            length={topic.subtopics.length}
                            removeDisabled={topic.subtopics.length === 1}
                            onMove={(direction) => setTopic(topicIndex, { subtopics: move(topic.subtopics, subtopicIndex, direction) })}
                            onRemove={() => setTopic(topicIndex, { subtopics: renumber(topic.subtopics.filter((_, index) => index !== subtopicIndex)) })}
                          />
                        </div>
                        <div className="course-form-grid">
                          <Field label="Subtopic title" required>
                            <input className="form-control" value={subtopic.title} onChange={(e) => setSubtopic(topicIndex, subtopicIndex, { title: e.target.value })} />
                          </Field>
                          <Field label="Subtopic description">
                            <input className="form-control" value={subtopic.description || ""} onChange={(e) => setSubtopic(topicIndex, subtopicIndex, { description: e.target.value })} />
                          </Field>
                        </div>

                        <div className="course-session-editor-list">
                          {subtopic.sessions.map((session, sessionIndex) => (
                            <div className="course-session-editor" key={`session-${topicIndex}-${subtopicIndex}-${sessionIndex}`}>
                              <span className="course-session-index">{sessionIndex + 1}</span>
                              <input
                                className="form-control"
                                aria-label="Session title"
                                placeholder="Session title"
                                value={session.title}
                                onChange={(e) => setSession(topicIndex, subtopicIndex, sessionIndex, { title: e.target.value })}
                              />
                              <input
                                className="form-control"
                                aria-label="Session description"
                                placeholder="Description"
                                value={session.description || ""}
                                onChange={(e) => setSession(topicIndex, subtopicIndex, sessionIndex, { description: e.target.value })}
                              />
                              <div className="input-group course-duration-input">
                                <input
                                  className="form-control"
                                  aria-label="Session duration"
                                  type="number"
                                  min="1"
                                  value={session.duration_minutes}
                                  onChange={(e) => setSession(topicIndex, subtopicIndex, sessionIndex, { duration_minutes: e.target.value })}
                                />
                                <span className="input-group-text">min</span>
                              </div>
                              <ItemActions
                                index={sessionIndex}
                                length={subtopic.sessions.length}
                                removeDisabled={subtopic.sessions.length === 1}
                                onMove={(direction) => setSubtopic(topicIndex, subtopicIndex, { sessions: move(subtopic.sessions, sessionIndex, direction) })}
                                onRemove={() => setSubtopic(topicIndex, subtopicIndex, { sessions: renumber(subtopic.sessions.filter((_, index) => index !== sessionIndex)) })}
                              />
                            </div>
                          ))}
                        </div>
                        <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => addSession(topicIndex, subtopicIndex)}>
                          <i className="bi bi-plus-lg me-2" />Add session
                        </button>
                      </div>
                    ))}
                  </div>
                  <button className="btn btn-sm btn-outline-primary" type="button" onClick={() => addSubtopic(topicIndex)}>
                    <i className="bi bi-plus-lg me-2" />Add subtopic
                  </button>
                </div>
              </details>
            ))}
          </div>
        </section>

        <div className="course-builder-actions">
          <Link className="btn btn-outline-secondary" to={edit ? `/courses/${courseId}` : "/courses"}>Cancel</Link>
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? "Saving…" : edit ? "Publish new version" : "Create and publish course"}
          </button>
        </div>
      </form>
    </main>
  );
}
